import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { emptyProjectState, ProjectState } from '@/business/project/store/project-state'
import {
  CLEAR_PROJECT,
  CREATE_PROJECT,
  CREATE_PROJECT_POST,
  DELETE_PROJECT_POST,
  FETCH_PROJECT,
  FETCH_PROJECT_POSTS,
  FETCH_PROJECTS,
  UPDATE_PROJECT,
  UPDATE_PROJECT_POST,
} from '@/business/project/store/types'
import {
  CreateProjectRequest,
  GetProjectsRequest,
  PostPageResponse,
  ProjectResponse,
  UpdateProjectRequest,
} from '@/api/generated'
import { Loading } from '@/shared/utils/vuex-loading'
import { Api } from '@/api/api'
import { emptyPageResponse, PageResponse } from '@/shared/interfaces/page-response'
import { ProjectLiteViewModel } from '@/business/project/interfaces/project-lite-view-model'
import { ProjectViewModel } from '@/business/project/interfaces/project-view-model'
import { Post } from '@/business/post/models/post'
import { CreateProjectPostPayload } from '@/business/project/store/models/create-project-post-payload'
import { updateOrDiscard, updateOrPrepend, withoutEntry } from '@/shared/utils/list'
import { UpdateProjectPostPayload } from '@/business/project/store/models/update-project-post-payload'
import { DeleteProjectPostPayload } from '@/business/project/store/models/delete-project-post-payload'
import { FetchProjectPostsPayload } from '@/business/project/store/models/fetch-project-posts-payload'
import { updatePostsPage } from '@/shared/utils/post-helpers/update-posts-page'
import { mergePages } from '@/shared/utils/post-helpers/merge-pages'
import { sortBy } from '@/shared/utils/sort-by'

const STORE_NAME = 'project'

@Module({
  name: STORE_NAME,
})
export class ProjectModule extends VuexModule {
  public static readonly STORE_NAME: string = STORE_NAME

  public data: ProjectState = emptyProjectState()

  public cleanup(): void {
    this.data = emptyProjectState()
  }

  @Loading(CREATE_PROJECT_POST)
  @Action({
    commit: CREATE_PROJECT_POST,
    rawError: true,
  })
  public createProjectPost({ post, projectId }: CreateProjectPostPayload): Promise<Post> {
    return Api.Projects.createProjectPost({
      projectId,
      postRequest: post,
    })
  }

  @Loading(DELETE_PROJECT_POST)
  @Action({
    commit: DELETE_PROJECT_POST,
    rawError: true,
  })
  deleteProjectPost(payload: DeleteProjectPostPayload): Promise<Post> {
    return Api.Posts.deletePost({
      postId: payload.post.id,
    }).then(() => payload.post)
  }

  @Loading(UPDATE_PROJECT_POST)
  @Action({
    commit: UPDATE_PROJECT_POST,
    rawError: true,
  })
  public updateProjectPost({ post, postId }: UpdateProjectPostPayload): Promise<Post> {
    return Api.Posts.updatePost({
      postId,
      postRequest: post,
    })
  }

  @Loading(CREATE_PROJECT)
  @Action({
    commit: CREATE_PROJECT,
    rawError: true,
  })
  public createProject(request: CreateProjectRequest): Promise<ProjectResponse> {
    return Api.Projects.createProject(request)
  }

  @Loading(FETCH_PROJECT)
  @Action({
    commit: FETCH_PROJECT,
    rawError: true,
  })
  public fetchProject(projectId: number): Promise<ProjectResponse> {
    return Api.Projects.getProject({
      projectId,
    })
  }

  @Loading(FETCH_PROJECT_POSTS)
  @Action({
    commit: FETCH_PROJECT_POSTS,
    rawError: true,
  })
  public async fetchProjectPosts({
    page,
    pageSize,
    projectId,
  }: FetchProjectPostsPayload): Promise<PostPageResponse> {
    return Api.Projects.getProjectPosts({
      page,
      pageSize,
      projectId,
    })
  }

  @Loading(FETCH_PROJECTS)
  @Action({
    commit: FETCH_PROJECTS,
    rawError: true,
  })
  public fetchProjects(request: GetProjectsRequest): Promise<PageResponse<ProjectLiteViewModel>> {
    return Api.Projects.getProjects(request).then(response => {
      return {
        ...response,
        items: response.items.map(item => ({
          description: item.shortDescription,
          id: item.id,
          image: item.image,
          name: item.name,
          organisation: item.organisation,
        })),
      }
    })
  }

  @Loading(UPDATE_PROJECT)
  @Action({
    commit: UPDATE_PROJECT,
    rawError: true,
  })
  public updateProject(request: UpdateProjectRequest): Promise<ProjectResponse> {
    return Api.Projects.updateProject(request)
  }

  @Mutation
  public [CLEAR_PROJECT](): void {
    this.data.project = emptyProjectState().project
  }

  @Mutation
  public [CREATE_PROJECT_POST](post: Post): void {
    this.data.project.posts = updatePostsPage(this.data.project.posts, post, updateOrPrepend, 1)
  }

  @Mutation
  public [CREATE_PROJECT](project: ProjectResponse) {
    this.data.project = mapProject(project)
  }

  @Mutation
  public [DELETE_PROJECT_POST](post: Post): void {
    this.data.project.posts = updatePostsPage(this.data.project.posts, post, withoutEntry, -1)
  }

  @Mutation
  public [FETCH_PROJECT](project: ProjectResponse) {
    this.data.project = mapProject(project)
  }

  @Mutation
  public [FETCH_PROJECT_POSTS](posts: PostPageResponse) {
    this.data.project.posts = mergePages(
      this.data.project.posts,
      posts,
      sortBy(a => a.createdAt.getTime(), 'desc')
    )
  }

  @Mutation
  public [FETCH_PROJECTS](projectPage: PageResponse<ProjectLiteViewModel>) {
    this.data.projects = projectPage.items
  }

  @Mutation
  public [UPDATE_PROJECT](project: ProjectResponse) {
    this.data.project = {
      ...mapProject(project),
      posts: this.data.project.posts,
    }
  }

  @Mutation
  public [UPDATE_PROJECT_POST](post: Post): void {
    this.data.project.posts = updatePostsPage(this.data.project.posts, post, updateOrDiscard)
  }
}

function mapProject(project: ProjectResponse): ProjectViewModel {
  return {
    ...project,
    gallery: project.resources.find(resource => resource.name === 'gallery')?.resources ?? [],
    posts: emptyPageResponse(),
  }
}
